import React from 'react';
import { Box, Typography, Paper, Avatar } from '@mui/material';
import {
  PersonOff as OfflineIcon,
  Repeat as ReuseIcon,
  Badge as BadgeIcon,
  Groups as GroupsIcon,
  Merge as MergeIcon,
} from '@mui/icons-material';
import { EducationModal, type EducationStep } from '../shared/EducationModal';

// Visual: Offline player concept
const OfflinePlayerConceptVisual: React.FC = () => (
  <Box sx={{ textAlign: 'center' }}>
    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, alignItems: 'center' }}>
      <Box sx={{ textAlign: 'center' }}>
        <Avatar sx={{ bgcolor: 'grey.400', width: 48, height: 48, mx: 'auto', mb: 0.5 }}>
          <OfflineIcon />
        </Avatar>
        <Typography variant="caption" display="block">Friend without</Typography>
        <Typography variant="caption" display="block">an account</Typography>
      </Box>
      <Typography variant="h5" color="text.secondary">=</Typography>
      <Box sx={{ textAlign: 'center' }}>
        <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48, mx: 'auto', mb: 0.5 }}>
          JD
        </Avatar>
        <Typography variant="caption" display="block">Offline Player</Typography>
        <Typography variant="caption" display="block">"John D."</Typography>
      </Box>
    </Box>
  </Box>
);

// Visual: Handle and customization
const CustomizationVisual: React.FC = () => (
  <Box sx={{ textAlign: 'center' }}>
    <Paper elevation={1} sx={{ p: 2, maxWidth: 200, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <Avatar sx={{ bgcolor: 'secondary.main', width: 40, height: 40 }}>MJ</Avatar>
        <Box sx={{ textAlign: 'left' }}>
          <Typography variant="body2" fontWeight="bold">Mike Johnson</Typography>
          <Typography variant="caption" color="text.secondary">Handle: MJ</Typography>
        </Box>
      </Box>
      <Typography variant="caption" color="text.secondary" display="block">
        Handle shows on the board grid
      </Typography>
    </Paper>
  </Box>
);

// Visual: Reuse across boards
const ReuseVisual: React.FC = () => (
  <Box sx={{ textAlign: 'center' }}>
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mb: 1 }}>
      <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36 }}>JD</Avatar>
    </Box>
    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
      {['Board 1', 'Board 2', 'Board 3'].map((board) => (
        <Paper key={board} elevation={0} sx={{ px: 1.5, py: 0.5, bgcolor: 'grey.100', border: 1, borderColor: 'grey.300' }}>
          <Typography variant="caption">{board}</Typography>
        </Paper>
      ))}
    </Box>
    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
      Same player, multiple boards
    </Typography>
  </Box>
);

// Visual: Bulk import
const BulkImportVisual: React.FC = () => (
  <Box sx={{ textAlign: 'center' }}>
    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, mb: 1 }}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Avatar key={i} sx={{ bgcolor: i <= 3 ? 'success.main' : 'grey.300', width: 28, height: 28, fontSize: 12 }}>
          {i <= 3 ? 'P' + i : '+'}
        </Avatar>
      ))}
    </Box>
    <Typography variant="caption" color="text.secondary">
      Import multiple players at once
    </Typography>
  </Box>
);

// Visual: Merge with online user
const MergeVisual: React.FC = () => (
  <Box sx={{ textAlign: 'center' }}>
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mb: 1 }}>
      <Box sx={{ textAlign: 'center' }}>
        <Avatar sx={{ bgcolor: 'grey.400', width: 40, height: 40, mx: 'auto', mb: 0.5 }}>
          <OfflineIcon fontSize="small" />
        </Avatar>
        <Typography variant="caption" display="block" fontSize={10}>Offline</Typography>
        <Typography variant="caption" display="block" fontSize={10}>"John D."</Typography>
      </Box>
      <MergeIcon sx={{ fontSize: 24, color: 'primary.main' }} />
      <Box sx={{ textAlign: 'center' }}>
        <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40, mx: 'auto', mb: 0.5 }}>
          JD
        </Avatar>
        <Typography variant="caption" display="block" fontSize={10}>Online</Typography>
        <Typography variant="caption" display="block" fontSize={10}>@john_doe</Typography>
      </Box>
    </Box>
    <Typography variant="caption" color="text.secondary" display="block">
      Transfer squares to their real account
    </Typography>
  </Box>
);

const steps: EducationStep[] = [
  {
    title: 'Offline Players',
    description: 'Create virtual players for friends and family who don\'t have accounts. They can participate in your boards just like regular users.',
    icon: <OfflineIcon sx={{ fontSize: 40 }} />,
    visual: <OfflinePlayerConceptVisual />,
  },
  {
    title: 'Customize Each Player',
    description: 'Give each offline player a display name and a short handle (1-4 characters) that appears on the board grid. Choose an icon to make them recognizable.',
    icon: <BadgeIcon sx={{ fontSize: 40 }} />,
    visual: <CustomizationVisual />,
  },
  {
    title: 'Reuse Across Boards',
    description: 'Once created, add the same offline player to any of your boards. Their balances are tracked across all games they participate in.',
    icon: <ReuseIcon sx={{ fontSize: 40 }} />,
    visual: <ReuseVisual />,
  },
  {
    title: 'Bulk Import',
    description: 'Running a large pool? Import multiple offline players at once to save time setting up your boards.',
    icon: <GroupsIcon sx={{ fontSize: 40 }} />,
    visual: <BulkImportVisual />,
  },
  {
    title: 'Merge with Online Users',
    description: 'When an offline player creates an account, merge their squares into their new account. Request a merge and the player approves to transfer ownership. Global merges update all boards at once.',
    icon: <MergeIcon sx={{ fontSize: 40 }} />,
    visual: <MergeVisual />,
  },
];

interface OfflinePlayersEducationModalProps {
  open: boolean;
  onClose: () => void;
}

export const OfflinePlayersEducationModal: React.FC<OfflinePlayersEducationModalProps> = ({ open, onClose }) => (
  <EducationModal
    open={open}
    onClose={onClose}
    modalId="offline_players"
    steps={steps}
    headerColor="secondary.main"
    finishButtonText="Manage Players"
    buttonColor="secondary"
  />
);

export default OfflinePlayersEducationModal;
